import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Coffee, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/12420819/pexels-photo-12420819.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Owner sign in · The Cozy Bean";
  }, []);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) toast.error(res.error);
    else toast.success("Welcome back");
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col p-8 md:p-14">
        <Link to="/" className="inline-flex items-center gap-2 text-[#1A1514]" data-testid="admin-login-brand-link">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#C05A46] text-[#F5F5F0]">
            <Coffee className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <span className="font-serif-display text-xl tracking-tight leading-none">The Cozy Bean</span>
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <p className="label-eyebrow">Owner access</p>
            <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tighter">
              Sign in to manage orders.
            </h1>
            <p className="mt-3 text-sm text-[#4A4341]">
              Live orders, statuses and the menu — all in one place.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-5">
              <div>
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  data-testid="admin-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
                />
              </div>
              <div>
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="admin-login-submit"
                className="btn-brand w-full rounded-full py-6 text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                {submitting ? "Signing in…" : "Sign in"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        <p className="text-xs text-[#7B7573]">© {new Date().getFullYear()} The Cozy Bean</p>
      </div>

      {/* Right: image */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="Barista" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1A1514]/60 via-[#1A1514]/20 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-[#F5F5F0]">
          <p className="label-eyebrow text-[#D69F4C]">Owner dashboard</p>
          <h2 className="mt-3 font-serif-display text-4xl tracking-tight max-w-md">
            One place to see every order, brewing to picked up.
          </h2>
        </div>
      </div>
    </div>
  );
}
